// SiloAlertsDot — Feb 24, 2026 (Randy — YTRE activity alert system).
//
// Evolved from PendingConfirmationsDot (Feb 2026 pt89h). Same visual
// (tiny coral 10px circle at top-2 right-2 of the silo card, sits above
// the silo art at z-40), same tap-to-open pattern, but now aggregates
// TWO notification sources into one dot:
//   • chore-confirmations (existing) — from calendar/pending-confirmations-summary
//   • YTRE unread replies (new)      — from ytre/activity/summary
//
// Coexistence rules (Randy's spec):
//   • Only one dot per card (no side-by-side crowding).
//   • Dot silent when both counts are zero.
//   • Tap: if only ONE source has activity, open that source's
//     dedicated flow directly (chores modal OR jump to YTRE tab).
//     If BOTH sources have activity, open a small popover with
//     sectioned rows so the user picks which to attend to.
//   • Aggregate count in the title tooltip so hover on desktop shows
//     the breakdown even without a click.
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PendingConfirmationsModal from "@/components/PendingConfirmationsModal";

export default function SiloAlertsDot({ silo, choreCount, ytreCount, ytreNewReplies, onChoreCountChanged, onYtreDismissed }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const chore = choreCount || 0;
  const ytre = ytreCount || 0;
  const total = chore + ytre;
  if (total <= 0) return null;

  const openTargetDirectly = () => {
    if (chore > 0 && ytre === 0) {
      setModalOpen(true);
    } else if (ytre > 0 && chore === 0) {
      // Jump straight into this silo's YTRE tab
      navigate(`/dashboard/silo/${silo.id}?tab=ytre`);
    } else {
      // Both — open the popover
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setPopoverPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 260) });
      }
      setPopoverOpen(true);
    }
  };

  const titleText = (() => {
    const parts = [];
    if (chore > 0) parts.push(`${chore} chore${chore === 1 ? "" : "s"} awaiting your OK`);
    if (ytre > 0) parts.push(`${ytreNewReplies || ytre} new YTRE repl${(ytreNewReplies || ytre) === 1 ? "y" : "ies"}`);
    return parts.join(" · ");
  })();

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); openTargetDirectly(); }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        title={titleText}
        data-testid={`silo-alerts-dot-${silo.slug}`}
        data-no-invert
        aria-label={titleText}
        className="absolute top-2 right-2 z-40 w-3 h-3 rounded-full bg-[#E86A5C] ring-2 ring-white shadow-[0_0_0_1px_rgba(0,0,0,0.35),0_2px_4px_rgba(0,0,0,0.4)] hover:scale-125 transition-transform"
      />
      {modalOpen && (
        <PendingConfirmationsModal
          siloId={silo.id}
          siloName={silo.name}
          onClose={() => setModalOpen(false)}
          onCountChanged={onChoreCountChanged}
        />
      )}
      {popoverOpen && popoverPos && createPortal(
        <>
          <div
            data-testid={`silo-alerts-popover-backdrop-${silo.slug}`}
            className="fixed inset-0 z-[70]"
            onClick={() => setPopoverOpen(false)}
          />
          <div
            data-testid={`silo-alerts-popover-${silo.slug}`}
            role="dialog"
            aria-label={`Alerts for ${silo.name}`}
            className="fixed z-[71] w-[280px] rounded-lg bg-white shadow-xl border hairline overflow-hidden"
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className="px-3 py-2 bg-[#F7FAF7] border-b hairline text-[11px] uppercase tracking-[0.22em] text-[#4A5D4E] font-medium">
              {silo.name}
            </div>
            {chore > 0 && (
              <div className="px-3 py-2.5 border-b hairline">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">Awaiting your OK</div>
                <button
                  type="button"
                  data-testid={`silo-alerts-popover-chores-${silo.slug}`}
                  onClick={() => { setPopoverOpen(false); setModalOpen(true); }}
                  className="w-full text-left flex items-center justify-between hover:bg-[#F0F1EE] rounded px-2 py-1.5 -mx-2"
                >
                  <span className="text-[12.5px] text-[#1C1B1A]">
                    {chore} chore{chore === 1 ? "" : "s"} need approval
                  </span>
                  <span className="text-[11px] text-[#2F6B3A] font-medium">Review →</span>
                </button>
              </div>
            )}
            {ytre > 0 && (
              <div className="px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">
                  YTRE — new replies
                </div>
                <button
                  type="button"
                  data-testid={`silo-alerts-popover-ytre-${silo.slug}`}
                  onClick={() => {
                    setPopoverOpen(false);
                    navigate(`/dashboard/silo/${silo.id}?tab=ytre`);
                  }}
                  className="w-full text-left flex items-center justify-between hover:bg-[#F0F1EE] rounded px-2 py-1.5 -mx-2"
                >
                  <span className="text-[12.5px] text-[#1C1B1A]">
                    {ytreNewReplies || ytre} new repl{(ytreNewReplies || ytre) === 1 ? "y" : "ies"} across {ytre} video{ytre === 1 ? "" : "s"}
                  </span>
                  <span className="text-[11px] text-[#2F6B3A] font-medium">Open YTRE →</span>
                </button>
                <button
                  type="button"
                  data-testid={`silo-alerts-popover-ytre-dismiss-${silo.slug}`}
                  onClick={async () => {
                    try {
                      await api.post(`/ytre/silo/${silo.id}/activity/dismiss`);
                      onYtreDismissed?.(silo.id);
                      toast.success("YTRE alerts dismissed for this silo.");
                    } catch { /* silent */ }
                    setPopoverOpen(false);
                  }}
                  className="mt-1 text-[10.5px] text-[#6B6A68] hover:text-[#1C1B1A] underline underline-offset-2 decoration-dotted"
                >
                  Dismiss all YTRE alerts
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
